// FrameMdi.js
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createRef,
} from "react";
import DynamicContent from "./frameWork";
import MainPage from "./MainPage";
import FrameTitle from "./frameTitle";

import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";

/**
 * MDI 영역 – 탭 / FrameTitle / 동적 콘텐츠를 세로로 배치합니다.
 */
const FrameMdi = ({
  goHome,
  tabs,
  activeTab,
  setActiveTab,
  openDirectUrl,
  onTabClick,
  onCloseTab,
  renderTabContent,
  tabComponentCache,
  sSidebarOpen,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  
  const tagsMain = useRef(null);
  const tagsMainBody = useRef(null);
  const [tagsBodyLeft, setTagsBodyLeft] = useState(0);

  const [showMain, setShowMain] = useState(true);
  const componentCache = useRef({});
  const componentRefs = useRef({});
  const [anchorEl, setAnchorEl] = useState(null);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);  

  const updateScrollState = useCallback(() => {
    const main = tagsMain.current;
    const body = tagsMainBody.current;

    if (!main || !body) return;

    const mainWidth = main.offsetWidth;
    const bodyWidth = body.scrollWidth;

    // 스크롤 자체가 없는 경우
    if (bodyWidth <= mainWidth) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }

    setCanScrollPrev(tagsBodyLeft < 0);
    setCanScrollNext(
      Math.abs(tagsBodyLeft) < bodyWidth - mainWidth
    );
  }, [tagsBodyLeft]);

  useEffect(() => {
    updateScrollState();
  }, [tabs.length, tagsBodyLeft, updateScrollState]);  

  useEffect(() => {
    if (activeTab) setShowMain(false);
    if (goHome) setShowMain(true);
  }, [activeTab, goHome]);

  useEffect(() => {
    const main = tagsMain.current;
    const body = tagsMainBody.current;

    if (main && body) {
      const mainWidth = main.offsetWidth;
      const bodyWidth = body.scrollWidth;

      if (bodyWidth > mainWidth) {
        setTagsBodyLeft(mainWidth - bodyWidth); // 오른쪽 끝으로 이동
      } else {
        setTagsBodyLeft(0); // 전체가 들어오면 처음으로 이동
      }
    }
  }, [tabs.length]); // 탭 개수가 바뀔 때만

  const openUrl = (node, addBtnList) => {
    openDirectUrl(node.MENU_ID, node.DISPLAY_PATH.split(" > "), node.PRGM_ID, node.PERM_ID, node.SYTM_FLAG_CD,
                node.PRGM_PATH, node.CMMNBTNPRINT, node.CMMNBTNADD, node.CMMNBTNDEL, node.CMMNBTNSAVE, 
                node.CMMNBTNEXCELDOWN, node.CMMNBTNSEARCH, node.PARM, null, addBtnList);
  };  

  const handleCloseTab = (menuId) => {
    delete componentCache.current[menuId];
    delete componentRefs.current[menuId];
    onCloseTab(menuId);
  };

  const getCurrentMenuId = useCallback(() => {
    if (showMain) return "Home";
    const cur = tabs.find((t) => t.menuId === activeTab);
    return cur ? cur.menuId : "";
  }, [showMain, tabs, activeTab]);  

  const getCurrentTitle = useCallback(() => {
    if (showMain) return "Home";
    const cur = tabs.find((t) => t.menuId === activeTab);
    return cur ? cur.fullPath : "";
  }, [showMain, tabs, activeTab]);

  const getCurrentPermissions = useCallback(() => {
    if (showMain)
      return {
        excel: false,
        select: false,
        read: false,
        save: false,
        del: false,
      };
    const currentTab = tabs.find((t) => t.menuId === activeTab);
    return (
      currentTab?.permissions ?? {
        excel: false,
        select: false,
        read: false,
        save: false,
        del: false,
      }
    );
  }, [showMain, tabs, activeTab]);

  const getAddButton = useCallback(() => {
    if (showMain) return [];
    const cur = tabs.find((t) => t.menuId === activeTab);
    return cur ? cur.addBtnList : [];
  }, [showMain, tabs, activeTab]);  

  const handleToolbarClick = (action) => {
    const cacheItem = tabComponentCache.current[activeTab];
    const ref = cacheItem ? cacheItem.ref : null;
    if (ref && ref.current && typeof ref.current.onToolbarClick === 'function') {
      ref.current.onToolbarClick(action);
    }
  };

  const closeCurrentTab = () => {
    if(!showMain && activeTab && activeTab !== "") {
      handleCloseTab(activeTab);
    }
  };  

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearTabs = () => {
    tabs.forEach((tab) => handleCloseTab(tab.menuId));
    setShowMain(true);
    setAnchorEl(null);
  };

  const handleMove = (offset) => {
    const mainWidth = tagsMain.current?.offsetWidth || 0;
    const mainBodyWidth = tagsMainBody.current?.offsetWidth || 0;

    if (mainWidth >= mainBodyWidth) return;
    const minOffset = 0;
    let newOffset = tagsBodyLeft + offset;

    if (offset > 0) {
      newOffset = Math.max(minOffset, tagsBodyLeft - offset);
    }
    setTagsBodyLeft(newOffset);
  };

  const handleScroll = (e) => {
    const { type } = e;
    let distance = 0;

    if (type === "wheel") {
      distance = e.deltaY ? e.deltaY * 2 : -(e.detail || 0) * 2;
    }

    handleMove(distance);
  };

  return (
    <Box
      display="flex"
      overflow="hidden"
      flexDirection="column"
      height="100%"
      maxWidth="100%"
      maxHeight="100%"
      boxSizing="border-box"
    >
      {/* 탭 영역 */}
      <Box
        display="flex"
        alignItems="center"
        height="32px"
        bgcolor="#ffffff"
        p={1}
        gap={1}
      >
        <IconButton
          disabled={!canScrollPrev}
          sx={{
            border: "1px solid #d9d9d9",
            padding: "3px",
            borderRadius: "2px",
            opacity: canScrollPrev ? 1 : 0.3,
          }}
          onClick={() => handleMove(150)}
          size="small"
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <div
          ref={tagsMain}
          onWheel={handleScroll}
          style={{
            zIndex: "200",
            display: "flex",
            justifyContent: "space-between",
            width: "calc(100% - 100px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            ref={tagsMainBody}
            style={{
              overflow: "visible",
              whiteSpace: "nowrap",
              transition: "transform 0.5s ease",
              transform: `translateX(${tagsBodyLeft}px)`,
            }}
          >
            <Box
              display="flex"
              height="32px"
              marginTop="2px"
              flex={1}
              overflow="hidden"
              color="#555"
              fontSize="12"
              gap={0.5}
            >
              <Tooltip title="홈">
                <Box
                  px={1}
                  py={1}
                  bgcolor={showMain ? "#1890ff" : "#fff"}
                  border={showMain ? "1px solid #ccc" : "1px solid #ccc"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowMain(true);
                    setActiveTab(null);
                  }}                  
                  //onClick={() => setShowMain(true)}
                  display="inline-block"
                  height="24px"
                  margin="3px 0px 3px 0px"
                  borderRadius="2px"
                  lineHeight="24px"
                  color={showMain ? "#fff" : "#555"}
                  overflow="hidden"
                >
                  <HomeOutlinedIcon
                    sx={{
                      fontSize: 14,
                      marginRight: "6px",
                      color: showMain ? "#fff" : "#999",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                    }}
                  >
                    Home
                  </span>
                </Box>
              </Tooltip>

              {tabs.map((tab) => (
                <Tooltip title={tab.menuName}>
                  <Box
                    key={tab.menuId}
                    px={1}
                    py={1}
                    borderRadius="2px"
                    margin="3px 0px 3px 0px"
                    height="24px"
                    lineHeight="24px"
                    overflow="hidden"
                    paddingRight="5px"
                    bgcolor={
                      activeTab === tab.menuId && !showMain ? "#1890ff" : "#fff"
                    }
                    color={
                      activeTab === tab.menuId && !showMain ? "#fff" : "#555"
                    }
                    border={
                      activeTab === tab.menuId && !showMain
                        ? "1px solid #1890ff"
                        : "1px solid #ccc"
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setShowMain(false);
                      onTabClick(tab.menuId);
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        verticalAlign: "baseline",
                        width: "8px",
                        height: "8px",
                        marginRight: "6px",
                        borderRadius: "50%",
                        background: "#dcdee0",
                        backgroundColor:
                          activeTab === tab.menuId && !showMain
                            ? "#fff"
                            : "#dcdee0",
                      }}
                    ></span>
                    <span
                      style={{
                        fontSize: 12,
                      }}
                    >
                      {tab.menuName}
                    </span>
                    <IconButton
                      style={{
                        marginLeft: "3px",
                        color:
                          activeTab === tab.menuId && !showMain
                            ? "#fff"
                            : "#555",
                      }}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseTab(tab.menuId);
                      }}
                    >
                      <CloseIcon style={{ fontSize: "14px" }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </div>
        </div>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            disabled={!canScrollNext}
            sx={{
              border: "1px solid #d9d9d9",
              padding: "3px",
              borderRadius: "2px",
              opacity: canScrollNext ? 1 : 0.3,
            }}
            onClick={() => handleMove(-150)}
            size="small"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          {/*}
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          {*/}
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            
            <MenuItem     
              onClick={() => {
                closeCurrentTab();
                handleMenuClose();
            }}>현재 탭 닫기</MenuItem>
            <MenuItem
              onClick={() => {
                handleClearTabs();
                handleMenuClose();
              }}
            >
              모든 탭 닫기
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* 제목 영역 */}
      <FrameTitle
        selectMenuId={getCurrentMenuId()}
        title={getCurrentTitle()}
        permissions={getCurrentPermissions()}
        onClickButton={handleToolbarClick}
        sSidebarOpen={sSidebarOpen}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
        addBtn={getAddButton()}
      />

      {/* 콘텐츠 영역 */}
      <Box
        flex={1}
        overflow="auto"
        bgcolor="#EFF0F5"
        p={0}
        borderRadius="0 0 12px 12px"
      >
        {tabs.map((tab) => {
          if (!componentCache.current[tab.menuId]) {
            const ref = createRef();
            componentRefs.current[tab.menuId] = ref;
            componentCache.current[tab.menuId] = (
              <DynamicContent
                menuId={tab.menuId}
                prgmPath={tab.prgmPath}
                permissions={tab.permissions}
                ref={ref}
                onCloseTab={() => handleCloseTab(tab.menuId)}
              />
            );
          }
          return (
            <Box
              key={tab.menuId}
              display={!showMain && activeTab === tab.menuId ? "block" : "none"}
              //height="calc(100vh - 148px)"
              height="100%"
            >
              {/* {componentCache.current[tab.menuId]} */}
              {renderTabContent(tab)}
            </Box>
          );
        })}

        <Box display={showMain ? "block" : "none"} height="100%" >
          <MainPage openurl={openUrl}/>
        </Box>
      </Box>
    </Box>
  );
};

export default FrameMdi;
